import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { Tag, Zap, Package, ShieldCheck, Rocket, Briefcase, Building2, Crown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { db } from '../firebase';

/* Admin-editable pricing AND card content for the company-setup (incorporate) +
   operate flows. Firestore doc `setupPricing/current`, edited on /admin/setup-pricing.

   Shape notes:
   - `plans` maps plan id → price and is SHARED by both flows, so a plan has one price
     everywhere. This is the original field; old documents keep working untouched.
   - `setupPlans` / `operatePlans` are the ordered cards each flow renders. They are
     separate lists because the two flows genuinely carry different copy for the same
     plan (e.g. `compliance` lists "1:1 Consultation" in the incorporate flow and
     "Automated transaction integrations" in the operate flow). Array order is display
     order.
   - `id` is a stable slug, not a label. The funnel branches on it — SetupPlan routes
     `starter` to /setup-expedited, SetupInvoice applies `expeditedFee` only to
     `starter`, and SetupUpgrade sells `compliance` — so ids must not be renamed even
     though every visible string around them is editable. PROTECTED_PLAN_IDS below is
     what the admin editor refuses to delete for that reason. */

export type PlanPrice = { priceYr: number; priceMo: number | null };

export type IconName = 'tag' | 'zap' | 'package' | 'shield' | 'rocket' | 'briefcase' | 'building' | 'crown';

export const PLAN_ICONS: Record<IconName, LucideIcon> = {
  tag: Tag, zap: Zap, package: Package, shield: ShieldCheck,
  rocket: Rocket, briefcase: Briefcase, building: Building2, crown: Crown,
};

export const ICON_NAMES = Object.keys(PLAN_ICONS) as IconName[];

/** Everything visible on one plan card. Price comes from `plans[id]`, not from here. */
export type PlanContent = {
  id: string;
  title: string;
  titleNote: string;
  description: string;
  featuresLabel: string;
  features: string[];
  badge: string | null;          // amber line above the price, e.g. "30-Day Free Trial"
  discountLabel: string | null;  // green pill beside the yearly price, e.g. "20% OFF"
  priceNote: string;             // small grey line under the price
  mostPopular: boolean;          // amber banner + border
  icon: IconName;
};

export type SetupPricing = {
  govFee: number;        // one-time government fee added to every setup/operate invoice
  govFeePCR: boolean;    // Phase 1: show the gov fee as "PCR" (Per Client Request) and exclude it from the total
  expeditedFee: number;  // one-time expedited-formation fee (Starter + expedited only)
  upgradePrice: number;  // Total Compliance upsell price shown in /setup-upgrade
  plans: Record<string, PlanPrice>;
  setupPlans: PlanContent[];
  operatePlans: PlanContent[];
};

/** Plan ids the funnel logic depends on — the editor blocks deleting these. */
export const PROTECTED_PLAN_IDS = ['starter', 'pulse', 'compliance', 'business-in-a-box'];

const GOV_NOTE = '(+ government fees)';

export const DEFAULT_SETUP_PLANS: PlanContent[] = [
  {
    id: 'starter', icon: 'tag',
    title: 'Starter', titleNote: '(For New Business Setup)',
    description: 'For first-time founders who need the basics to launch with confidence.',
    featuresLabel: 'Core Features:',
    features: ['Include features like DED/Free Zone registration,  license issuance, VAT registration, etc.'],
    badge: 'Free Trial 30-Days', discountLabel: null, priceNote: GOV_NOTE, mostPopular: false,
  },
  {
    id: 'compliance', icon: 'zap',
    title: 'Total Compliance', titleNote: '',
    description: 'For Entrepreneurs who want to stay 100% compliant with UAE regulations.',
    featuresLabel: 'Everything in Starter +',
    features: [
      'VAT registration + quarterly VAT filings',
      'Accounting & bookkeeping dashboard (cash flow, P&L, balance sheet)',
      'UAE Corporate Tax (CT) registration & annual return filing (FTA)',
      'Includes up to 100 bookkeeping transactions per month',
      'Annual financial statements',
      '1:1 Consultation with Certified Accountant',
    ],
    badge: null, discountLabel: '20% OFF', priceNote: GOV_NOTE, mostPopular: false,
  },
  {
    id: 'business-in-a-box', icon: 'package',
    title: 'Business-in-a-Box™', titleNote: '',
    description: 'For Entrepreneurs who want full legal, accounting, and tax compliance, plus a dedicated accountant.',
    featuresLabel: 'Everything in Total Compliance +',
    features: [
      'Includes up to 200 bookkeeping transactions per month',
      'Quarterly compliance advisory session',
      'Investor-ready reporting (deck financials, data room prep)',
      'Priority support',
    ],
    badge: null, discountLabel: '20% OFF', priceNote: GOV_NOTE, mostPopular: true,
  },
];

export const DEFAULT_OPERATE_PLANS: PlanContent[] = [
  {
    id: 'pulse', icon: 'tag',
    title: 'Pulse', titleNote: '(for Existing Businesses)',
    description: 'For founders who already have a registered business and want to stay compliant, investor-ready, and growth-focused with the right legal and financial tools.',
    featuresLabel: 'Includes:',
    features: [
      'Business Health & Investor Readiness Report',
      'Legal & Compliance Check (Trade License, MOA, VAT)',
      'Corporate Tax Registration & Advisory',
      'Access to Business Templates (NDAs, MOA, Resolutions)',
    ],
    badge: '30-Day Free Trial', discountLabel: null, priceNote: GOV_NOTE, mostPopular: false,
  },
  {
    id: 'compliance', icon: 'zap',
    title: 'Total Compliance', titleNote: '',
    description: 'For Entrepreneurs who want to stay 100% compliant with UAE regulations.',
    featuresLabel: 'Everything in Starter +',
    features: [
      'VAT registration + quarterly VAT filings',
      'Accounting & bookkeeping dashboard (cash flow, P&L, balance sheet)',
      'UAE Corporate Tax (CT) registration & annual return filing (FTA)',
      'Includes up to 100 bookkeeping transactions per month',
      'Automated transaction integrations (Stripe, Shopify, Lean, etc.)',
      'Annual financial statements (CFO-ready)',
    ],
    badge: null, discountLabel: '20% OFF', priceNote: GOV_NOTE, mostPopular: false,
  },
  {
    id: 'business-in-a-box', icon: 'package',
    title: 'Business-in-a-Box™', titleNote: '',
    description: 'For Entrepreneurs who want full legal, accounting, and tax compliance, plus a dedicated accountant.',
    featuresLabel: 'Everything in Total Compliance +',
    features: [
      'Corporate Tax (CT) registration & annual return filing (FTA) + quarterly tax planning & estimates',
      'Includes up to 200 bookkeeping transactions per month',
      'Quarterly compliance advisory session',
      'Investor-ready reporting (deck financials, data room prep)',
      'Priority support',
    ],
    badge: null, discountLabel: '20% OFF', priceNote: GOV_NOTE, mostPopular: true,
  },
];

export const DEFAULT_SETUP_PRICING: SetupPricing = {
  govFee: 300,
  govFeePCR: true, // Phase 1 default — gov fees depend on the client's activity, so shown as PCR
  expeditedFee: 300,
  upgradePrice: 7350,
  plans: {
    starter: { priceYr: 1100, priceMo: null },
    pulse: { priceYr: 1100, priceMo: null },
    compliance: { priceYr: 14399, priceMo: 1499 },
    'business-in-a-box': { priceYr: 22999, priceMo: 2399 },
  },
  setupPlans: DEFAULT_SETUP_PLANS,
  operatePlans: DEFAULT_OPERATE_PLANS,
};

export const NEW_PLAN_CONTENT = (id: string): PlanContent => ({
  id,
  title: 'New plan', titleNote: '',
  description: '',
  featuresLabel: 'Includes:',
  features: [],
  badge: null, discountLabel: null, priceNote: GOV_NOTE, mostPopular: false, icon: 'tag',
});

/** Coerce one stored card into a complete PlanContent, filling gaps from `fallback`. */
function normalisePlan(raw: any, fallback: PlanContent): PlanContent {
  if (!raw || typeof raw !== 'object' || !raw.id) return fallback;
  return {
    id: String(raw.id),
    title: typeof raw.title === 'string' ? raw.title : fallback.title,
    titleNote: typeof raw.titleNote === 'string' ? raw.titleNote : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    featuresLabel: typeof raw.featuresLabel === 'string' ? raw.featuresLabel : 'Includes:',
    features: Array.isArray(raw.features) ? raw.features.filter((f: any) => typeof f === 'string') : [],
    badge: typeof raw.badge === 'string' && raw.badge ? raw.badge : null,
    discountLabel: typeof raw.discountLabel === 'string' && raw.discountLabel ? raw.discountLabel : null,
    priceNote: typeof raw.priceNote === 'string' ? raw.priceNote : GOV_NOTE,
    mostPopular: raw.mostPopular === true,
    icon: (ICON_NAMES as string[]).includes(raw.icon) ? raw.icon : 'tag',
  };
}

/** Read a stored card list, falling back to defaults when absent or empty. */
function normaliseList(raw: any, defaults: PlanContent[]): PlanContent[] {
  if (!Array.isArray(raw) || !raw.length) return defaults;
  const byId = new Map(defaults.map(d => [d.id, d]));
  return raw
    .filter(r => r && typeof r === 'object' && r.id)
    .map(r => normalisePlan(r, byId.get(String(r.id)) ?? NEW_PLAN_CONTENT(String(r.id))));
}

/** Merge a raw Firestore payload into a complete SetupPricing. Exported so the admin
    editor loads through exactly the same normalisation the public flows use. */
export function normaliseSetupPricing(d: Partial<SetupPricing> | any): SetupPricing {
  return {
    govFee: typeof d?.govFee === 'number' ? d.govFee : DEFAULT_SETUP_PRICING.govFee,
    govFeePCR: typeof d?.govFeePCR === 'boolean' ? d.govFeePCR : DEFAULT_SETUP_PRICING.govFeePCR,
    expeditedFee: typeof d?.expeditedFee === 'number' ? d.expeditedFee : DEFAULT_SETUP_PRICING.expeditedFee,
    upgradePrice: typeof d?.upgradePrice === 'number' ? d.upgradePrice : DEFAULT_SETUP_PRICING.upgradePrice,
    plans: { ...DEFAULT_SETUP_PRICING.plans, ...(d?.plans || {}) },
    setupPlans: normaliseList(d?.setupPlans, DEFAULT_SETUP_PLANS),
    operatePlans: normaliseList(d?.operatePlans, DEFAULT_OPERATE_PLANS),
  };
}

export function useSetupPricing(): SetupPricing {
  const [p, setP] = useState<SetupPricing>(DEFAULT_SETUP_PRICING);
  useEffect(() => {
    const ref = doc(db, 'setupPricing', 'current');
    return onSnapshot(ref, snap => {
      if (!snap.exists()) return;
      setP(normaliseSetupPricing(snap.data()));
    }, () => { /* permission/transient errors: keep defaults */ });
  }, []);
  return p;
}
